import { useRef, useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function useAnim(threshold = 0.1) {
  const ref = useRef<HTMLElement | null>(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setV(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, v };
}

export default function AboutPage() {
  const s1 = useAnim(0.1);
  const s2 = useAnim(0.1);
  const s3 = useAnim(0.08);
  const s4 = useAnim(0.1);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        {/* Page Hero */}
        <section className="relative h-72 md:h-96 lg:h-[480px] flex items-center justify-center overflow-hidden">
          <img src="https://public.readdy.ai/ai/img_res/f196c1e32dd8430a48542451664e6812.jpg" alt="지부 소개" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 text-center px-5">
            <div className="page-hero-badge flex items-center justify-center gap-2 mb-3">
              <span className="w-6 h-px bg-[#C9A84C]" />
              <span className="text-[#C9A84C] text-xs font-bold tracking-widest uppercase">About Us</span>
              <span className="w-6 h-px bg-[#C9A84C]" />
            </div>
            <h1 className="page-hero-title text-3xl md:text-4xl lg:text-5xl font-black text-white">지부 소개</h1>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-[#F4F6FA] border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-5 md:px-6 py-3 flex items-center gap-2 text-xs text-gray-500">
            <i className="ri-home-line" /><span>홈</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-[#1B3A6B] font-semibold">지부 소개</span>
          </div>
        </div>

        {/* 인사말 */}
        <section ref={s1.ref as React.RefObject<HTMLElement>} className="py-14 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
              <div className={`relative anim-slide-left ${s1.v ? 'is-visible' : ''}`}>
                <div className="rounded-xl overflow-hidden w-full h-72 md:h-96 border border-gray-100">
                  <img src="https://readdy.ai/api/search-image?query=professional%20Korean%20male%20director%20of%20beauty%20academy%20in%20formal%20dark%20suit%2C%20confident%20warm%20smile%2C%20modern%20bright%20office%20background%20with%20certificates%20and%20awards%20on%20wall%2C%20professional%20portrait%20photography%2C%20authoritative%20and%20trustworthy%20appearance%2C%20clean%20white%20background&width=600&height=500&seq=director_new1&orientation=portrait" alt="지부장 인사" className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute -bottom-5 left-6 bg-[#1B3A6B] text-white rounded-lg px-5 py-3">
                  <p className="font-black text-base">김병희</p>
                  <p className="text-xs text-white/70 mt-0.5">대한문신사중앙회 중구지부장</p>
                </div>
              </div>
              <div className={`mt-8 lg:mt-0 anim-slide-right ${s1.v ? 'is-visible' : ''}`} style={{ transitionDelay: '100ms' }}>
                <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-6">인사말</span>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#1B3A6B] mb-6 leading-tight">전문 아티스트의 꿈,<br /><span className="text-[#C9A84C]">중구지부와 함께</span></h2>
                <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                  <p>안녕하십니까. 대한문신사중앙회 중구지부장 김병희입니다.</p>
                  <p>저희 중구지부는 보건복지부 정식 인가를 받은 (사)대한문신사중앙회의 공식 지부로서, 반영구 문신(두피·입술·아이라인) 분야의 전문 아티스트 양성을 목표로 설립되었습니다.</p>
                  <p>15년 이상의 교육 경험을 바탕으로 체계적인 커리큘럼과 실무 중심의 교육을 제공하며, 수료 후 본회 공식 자격증 취득부터 창업 컨설팅까지 수강생의 성공적인 커리어를 전폭 지원합니다.</p>
                  <p>여러분의 꿈을 현실로 만들어 드리겠습니다. 언제든지 문을 두드려 주세요.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 본회 안내 */}
        <section ref={s2.ref as React.RefObject<HTMLElement>} className="py-14 md:py-20 bg-[#F4F6FA]">
          <div className="max-w-7xl mx-auto px-5 md:px-6">
            <div className={`text-center mb-10 md:mb-14 anim-fade-up ${s2.v ? 'is-visible' : ''}`}>
              <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-4">본회 안내</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#1B3A6B] mb-4">(사)대한문신사중앙회</h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">보건복지부 정식 인가 단체로서 전국 문신사의 권익 보호와 기술 발전을 이끌고 있습니다</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {[
                { icon: 'ri-government-line', title: '정부 정식 인가', desc: '보건복지부로부터 정식 인가를 받은 공식 사단법인으로, 발급하는 자격증은 법적 공신력을 갖습니다.' },
                { icon: 'ri-map-pin-line', title: '전국 지부 네트워크', desc: '서울, 부산, 대구, 인천 등 전국 주요 도시에 공식 지부를 운영하며 지역 밀착형 교육을 제공합니다.' },
                { icon: 'ri-trophy-line', title: '업계 최고 공신력', desc: '15년 이상의 역사와 1만여 명의 회원을 보유한 국내 최대 문신사 단체로 업계 표준을 선도합니다.' },
              ].map((item, i) => (
                <div key={item.title} className={`bg-white rounded-xl p-6 md:p-8 border border-gray-100 hover:border-[#1B3A6B]/20 transition-all card-lift anim-scale-up ${s2.v ? 'is-visible' : ''}`} style={{ transitionDelay: s2.v ? `${150 + i * 100}ms` : '0ms' }}>
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1B3A6B]/10 text-[#1B3A6B] mb-5"><i className={`${item.icon} text-xl`} /></div>
                  <h3 className="text-base font-bold text-[#1B3A6B] mb-3">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 공식 인증 마크 섹션 */}
        <section ref={s3.ref as React.RefObject<HTMLElement>} className="py-14 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-6">
            <div className={`text-center mb-10 md:mb-14 anim-fade-up ${s3.v ? 'is-visible' : ''}`}>
              <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-4">공식 인증</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#1B3A6B] mb-4">본회 공식 인증 마크</h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">대한문신사중앙회 중구지부는 아래 인증 및 허가를 보유한 공신력 있는 공식 교육기관입니다</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              <div className={`relative rounded-xl overflow-hidden border border-gray-100 bg-[#F4F6FA] flex items-center justify-center p-6 md:p-8 anim-slide-left ${s3.v ? 'is-visible' : ''}`} style={{ transitionDelay: '100ms' }}>
                <div className="w-full max-w-sm">
                  <img src="https://readdy.ai/api/search-image?query=official%20Korean%20government%20issued%20certificate%20document%20with%20gold%20embossed%20seal%2C%20navy%20blue%20border%20frame%2C%20formal%20typography%2C%20Ministry%20of%20Culture%20Sports%20and%20Tourism%20official%20stamp%2C%20prestigious%20certification%20paper%20on%20clean%20white%20background%2C%20high%20quality%20document%20photography&width=600&height=420&seq=cert_mark_v1&orientation=landscape" alt="보건복지부 정식 인가 인증서" className="w-full h-auto rounded-lg border border-gray-200" />
                  <div className="mt-4 flex items-center justify-center gap-2 bg-[#1B3A6B]/5 border border-[#1B3A6B]/10 rounded-lg px-4 py-2.5">
                    <i className="ri-verified-badge-line text-[#C9A84C]" />
                    <span className="text-[#1B3A6B] text-xs font-bold">보건복지부 정식 인가 인증서</span>
                  </div>
                </div>
              </div>
              <div className={`flex flex-col justify-center gap-4 md:gap-5 anim-slide-right ${s3.v ? 'is-visible' : ''}`} style={{ transitionDelay: '150ms' }}>
                {[
                  { icon: 'ri-government-line', badge: '정부 인가', title: '보건복지부 정식 인가', desc: '대한민국 보건복지부로부터 정식 인가를 받은 사단법인으로, 발급하는 모든 자격증과 이수증은 법적 효력을 갖습니다.', color: 'bg-[#1B3A6B]' },
                  { icon: 'ri-shield-check-line', badge: '공식 지부', title: '(사)대한문신사중앙회 공식 인증 지부', desc: '본회로부터 공식 지부 인증을 받아 본회의 교육 기준과 윤리 강령을 준수하며 운영됩니다.', color: 'bg-[#C9A84C]' },
                  { icon: 'ri-heart-pulse-line', badge: '위생 인증', title: '보건복지부 위생교육 지정 기관', desc: '문신사 필수 위생교육을 공식 이수할 수 있는 보건복지부 지정 교육기관으로 이수증을 즉시 발급합니다.', color: 'bg-[#1B3A6B]' },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4 p-4 md:p-5 rounded-xl border border-gray-100 bg-[#F4F6FA] hover:border-[#1B3A6B]/20 transition-all">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${item.color} text-white flex-shrink-0`}><i className={`${item.icon} text-base`} /></div>
                    <div>
                      <div className="flex items-center gap-2 mb-1"><span className="text-xs font-bold text-[#C9A84C] bg-[#C9A84C]/10 px-2 py-0.5 rounded-sm">{item.badge}</span></div>
                      <p className="font-bold text-[#1B3A6B] text-sm mb-1">{item.title}</p>
                      <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={`bg-[#1B3A6B] rounded-xl px-6 md:px-8 py-8 anim-fade-up ${s3.v ? 'is-visible' : ''}`} style={{ transitionDelay: s3.v ? '300ms' : '0ms' }}>
              <p className="text-white/60 text-xs font-bold tracking-widest uppercase text-center mb-7">공식 인증 현황</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {[
                  { icon: 'ri-government-line', title: '보건복지부', sub: '정식 인가 단체' },
                  { icon: 'ri-award-line', title: '자격증 발급 기관', sub: '두피·입술·아이라인 반영구 문신' },
                  { icon: 'ri-heart-pulse-line', title: '위생교육 지정', sub: '보건복지부 인정' },
                  { icon: 'ri-map-pin-2-line', title: '서울 중구 공식 지부', sub: '(사)대한문신사중앙회 인증' },
                ].map((mark, i) => (
                  <div key={mark.title} className={`flex flex-col items-center text-center p-4 md:p-5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors anim-scale-up ${s3.v ? 'is-visible' : ''}`} style={{ transitionDelay: s3.v ? `${350 + i * 80}ms` : '0ms' }}>
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-[#C9A84C]/20 mb-3"><i className={`${mark.icon} text-[#C9A84C] text-lg md:text-xl`} /></div>
                    <p className="text-white font-bold text-xs md:text-sm mb-1">{mark.title}</p>
                    <p className="text-white/50 text-xs">{mark.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 오시는 길 */}
        <section ref={s4.ref as React.RefObject<HTMLElement>} className="py-14 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-6">
            <div className={`text-center mb-10 md:mb-14 anim-fade-up ${s4.v ? 'is-visible' : ''}`}>
              <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-4">오시는 길</span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#1B3A6B]">찾아오시는 방법</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-start">
              <div className={`anim-slide-left ${s4.v ? 'is-visible' : ''}`} style={{ transitionDelay: '100ms' }}>
                {/* 업로드된 지도 이미지 */}
                <div className="relative rounded-xl overflow-hidden border border-gray-200">
                  <img
                    src="https://storage.readdy-site.link/project_files/c207dbb9-8e25-402c-98cd-353ca372c5a7/c5e805ac-d2b7-4ba6-b812-6a54b297b614_unnamed.png?v=dd27a0bf3d52a42d8154e9f364760606"
                    alt="서울 중구 명동3길 5 위드빌딩 위치 지도"
                    className="w-full h-auto block"
                  />
                  {/* 하단 주소 바 */}
                  <div className="absolute bottom-0 left-0 right-0 flex items-center gap-2 px-4 py-3" style={{ background: 'rgba(27,58,107,0.92)' }}>
                    <i className="ri-map-pin-2-fill text-[#C9A84C]" />
                    <span className="text-white text-xs font-semibold">서울 중구 명동3길 5, 위드빌딩 6층</span>
                  </div>
                </div>
                {/* 지도 바로가기 버튼 */}
                <div className="flex gap-3 mt-4">
                  <a
                    href="https://map.naver.com/p/search/%EC%84%9C%EC%9A%B8%20%EC%A4%91%EA%B5%AC%20%EB%AA%85%EB%8F%993%EA%B8%B8%205%20%EC%9C%84%EB%93%9C%EB%B9%8C%EB%94%A9"
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-white text-sm font-bold cursor-pointer whitespace-nowrap transition-colors"
                    style={{ background: '#03C75A' }}
                  >
                    <i className="ri-map-2-line text-base" />
                    네이버 지도로 보기
                  </a>
                  <a
                    href="https://map.kakao.com/?q=%EC%84%9C%EC%9A%B8%20%EC%A4%91%EA%B5%AC%20%EB%AA%85%EB%8F%993%EA%B8%B8%205%20%EC%9C%84%EB%93%9C%EB%B9%8C%EB%94%A9"
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-[#3C1E1E] text-sm font-bold cursor-pointer whitespace-nowrap transition-colors"
                    style={{ background: '#FEE500' }}
                  >
                    <i className="ri-map-pin-2-line text-base" />
                    카카오맵으로 보기
                  </a>
                </div>
              </div>
              <div className="space-y-4 md:space-y-5">
                {[
                  { icon: 'ri-map-pin-line', title: '주소', content: '서울 중구 명동3길 5, 위드빌딩 6층' },
                  { icon: 'ri-phone-line', title: '전화', content: '010-6740-4061' },
                  { icon: 'ri-time-line', title: '운영시간', content: '평일 09:00 ~ 18:00\n토요일 10:00 ~ 15:00 (일요일·공휴일 휴무)' },
                  { icon: 'ri-subway-line', title: '지하철', content: '4호선 명동역 6번 출구 도보 5분\n2호선 을지로입구역 6번 출구 도보 3분' },
                ].map((item, i) => (
                  <div key={item.title} className={`flex gap-4 p-4 rounded-lg bg-[#F4F6FA] border border-gray-100 anim-fade-up ${s4.v ? 'is-visible' : ''}`} style={{ transitionDelay: s4.v ? `${150 + i * 80}ms` : '0ms' }}>
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1B3A6B]/10 text-[#1B3A6B] flex-shrink-0"><i className={`${item.icon} text-sm`} /></div>
                    <div>
                      <p className="font-bold text-[#1B3A6B] text-sm mb-1">{item.title}</p>
                      <p className="text-gray-500 text-sm leading-relaxed whitespace-pre-line">{item.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
